// Custom hook for test patient creation

import { useState, useCallback } from 'react';
import { TEST_BEACONS } from '../constants';
import type { Physician, ProcedureType, BeaconToPatientMap, PatientCreationResult } from '../types';

export interface UsePatientCreationReturn {
  creatingPatients: boolean;
  patientCreationStatus: string | null;
  createTestPatientsWithBracelets: () => Promise<void>;
}

interface UsePatientCreationProps {
  physicians: Physician[];
  procedureTypes: ProcedureType[];
  beaconToPatientMap: BeaconToPatientMap;
  createPatient: (options: unknown) => Promise<unknown>;
  refetchPatients: () => void;
}

export const usePatientCreation = ({
  physicians,
  procedureTypes,
  beaconToPatientMap,
  createPatient,
  refetchPatients,
}: UsePatientCreationProps): UsePatientCreationReturn => {
  const [creatingPatients, setCreatingPatients] = useState(false);
  const [patientCreationStatus, setPatientCreationStatus] = useState<string | null>(null);

  const createTestPatientsWithBracelets = useCallback(async () => {
    if (physicians.length === 0) {
      setPatientCreationStatus('Error: No physicians found. Please add a physician first.');
      return;
    }

    setCreatingPatients(true);
    setPatientCreationStatus('Creating test patients...');

    const physician = physicians[0];
    const procedureType = procedureTypes.length > 0 ? procedureTypes[0] : null;

    // Only create patients for patient beacons (not staff/physician beacons)
    const patientBeacons = TEST_BEACONS.filter(b => b.id.includes('_PATIENT_'));

    const results: PatientCreationResult[] = [];

    for (let i = 0; i < patientBeacons.length; i++) {
      const beacon = patientBeacons[i];
      const existingPatient = beaconToPatientMap[beacon.id];

      if (existingPatient) {
        results.push({
          beacon: beacon.id,
          status: 'skipped',
          reason: `Already assigned to ${existingPatient.name}`,
        });
        continue;
      }

      const patientName = beacon.entityName || `Test Patient ${i + 1}`;

      try {
        // Create patient directly with bracelet assigned
        const result = await createPatient({
          variables: {
            name: patientName,
            braceletId: beacon.id,
            physician: physician.id,
            procedureType: procedureType?.id,
          },
        });

        const patientId = (result as { data?: { createPatient?: { id?: number } } })?.data?.createPatient?.id;

        if (patientId) {
          results.push({ beacon: beacon.id, status: 'created', patientId });
        } else {
          results.push({ beacon: beacon.id, status: 'error', reason: 'No patient ID returned' });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        results.push({ beacon: beacon.id, status: 'error', reason: errorMessage });
      }
    }

    const created = results.filter(r => r.status === 'created').length;
    const skipped = results.filter(r => r.status === 'skipped').length;
    const errors = results.filter(r => r.status === 'error').length;

    setPatientCreationStatus(`Done! Created: ${created}, Skipped: ${skipped}, Errors: ${errors}`);
    setCreatingPatients(false);

    // Refetch patients to see the new assignments
    setTimeout(() => refetchPatients(), 1000);
  }, [physicians, procedureTypes, beaconToPatientMap, createPatient, refetchPatients]);

  return {
    creatingPatients,
    patientCreationStatus,
    createTestPatientsWithBracelets,
  };
};
